<template>
  <v-dialog v-model="show" width="775px">
    <div class="modal" style="height: 480px">
      <v-row>
        <div class="icon-modal table-cell" style="height: 480px">
          <v-layout class="align-center">
            <v-flex>
              <img class="custom-cancel" style="top:-110px" :src="require('@/assets/dele.png')" alt=""/>
            </v-flex>
          </v-layout>
        </div>
        <v-col class="table-cell modal-content">
          <div class="d-flex justify-space-between">
            <h6 class="modal-title mb-5">Cancel Subscription</h6>
          </div>
          <div class="content_cont_modal">
            <!-- CALCULATED tenants — Stripe subscription. Release flow with
                 30-day window + prorated refund. -->
            <template v-if="!is_free_mode && !confirmation">
              <p class="custom-cancel owner cancel-paragraph">
                Cancellation takes effect 30 days from today. Your market exclusivity
                is preserved through that period.
              </p>
              <p class="custom-cancel owner cancel-paragraph">
                Any unused days within the final billing cycle will be refunded
                prorated.
              </p>
              <p class="custom-cancel owner cancel-paragraph">
                Would you like to proceed with the cancellation of your
                subscription?
              </p>
            </template>

            <!-- FREE-mode tenants — no Stripe subscription. Falls back to the
                 legacy delete-account flow so the legacy copy still applies. -->
            <template v-else-if="is_free_mode && !confirmation">
              <p class="custom-cancel owner cancel-paragraph">
                Cancelling your account will disable access to the application
                for all of your users.
              </p>
              <p class="custom-cancel owner cancel-paragraph">
                Vendors and homeowners will be notified that you have removed
                yourself and your users.
              </p>
              <p class="custom-cancel owner cancel-paragraph">
                Would you like to proceed with the cancellation of your account?
              </p>
            </template>

            <!-- Post-release confirmation. Shows the cancel-effective date and
                 the prorated refund amount returned by /v1/markets/release. -->
            <template v-else>
              <p class="custom-cancel owner cancel-paragraph">
                Your cancellation is scheduled. You&rsquo;ll keep full access until
                <strong>{{ formatted_cancel_effective_at }}</strong>.
              </p>
              <p v-if="formatted_refund" class="custom-cancel owner cancel-paragraph">
                A prorated refund of <strong>{{ formatted_refund }}</strong> will be
                issued to your original payment method.
              </p>
              <p class="custom-cancel owner cancel-paragraph">
                Your market exclusivity is preserved through the cancellation date.
              </p>
            </template>

            <div v-if="!confirmation" class="cancel-privacy-row">
              <span class="cancel-privacy-text">Want a copy of your data first?</span>
              <button
                  v-if="!exportRequested"
                  type="button"
                  class="cancel-privacy-link"
                  :disabled="exporting"
                  @click="onRequestExport"
              >{{ exporting ? 'Requesting…' : 'Export my data' }}</button>
              <span v-else class="cancel-privacy-success">Export requested — we'll email it.</span>
            </div>
            <p v-if="exportError" class="cancel-privacy-error">{{ exportError }}</p>
            <p v-if="error_message" class="cancel-privacy-error">{{ error_message }}</p>
          </div>

          <div class="d-flex" style="position: relative;left: 3px;top: 13px;">
            <template v-if="!confirmation">
              <v-btn
                  class="btn-blanco mr-6 custom-cancel"
                  rounded
                  depressed
                  :loading="submitting"
                  :disabled="submitting"
                  @click="confirm_cancel"
              >yes
              </v-btn>
              <v-btn
                  class="btn-azul custom-cancel owner"
                  rounded
                  depressed
                  @click="close"
              >no
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                  class="btn-azul custom-cancel owner"
                  rounded
                  depressed
                  @click="close"
              >Got it
              </v-btn>
            </template>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-dialog>
</template>
<style scoped>
.cancel-paragraph {
  font-family: Roboto, Arial, sans-serif !important;
  font-weight: 400 !important;
  margin-bottom: 12px;
}
.cancel-privacy-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  font-family: Roboto, Arial, sans-serif;
  font-size: 13px;
}
.cancel-privacy-text { color: #5e6b85; }
.cancel-privacy-link {
  background: none;
  border: none;
  color: #2bace2;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
.cancel-privacy-link:disabled { opacity: 0.5; cursor: not-allowed; text-decoration: none; }
.cancel-privacy-success { color: #2e7d32; font-weight: 500; }
.cancel-privacy-error { color: #c0392b; font-size: 12px; margin: 6px 0 0; }
</style>
<script>
import { requestDataExport } from "@/util/data_export";

export default {
  props: ['show', 'hide', 'pricingMode'],
  data: () => ({
    image: require("@/assets/logo.svg"),
    exporting: false,
    exportRequested: false,
    exportError: '',
    submitting: false,
    error_message: '',
    confirmation: null, // { cancel_effective_at, prorated_refund_amount }
  }),
  computed: {
    is_free_mode() {
      // Default to "calculated" so a missing pricingMode doesn't accidentally
      // route to the legacy delete-account path. Callers should pass through
      // the tenant's pricing_mode explicitly.
      return this.pricingMode === 'free';
    },
    formatted_cancel_effective_at() {
      const ts = this.confirmation && this.confirmation.cancel_effective_at;
      if (!ts) return '';
      // release_market.js returns cancel_effective_at as ms (Date.now() +
      // 30d ms). Heuristic: anything > 1e12 is already ms; smaller is
      // Unix seconds. Date.parse handles ISO strings.
      const ms = typeof ts === 'number'
        ? (ts > 1e12 ? ts : ts * 1000)
        : Date.parse(ts);
      if (!ms || isNaN(ms)) return '';
      const date = new Date(ms);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    formatted_refund() {
      const cents = this.confirmation && this.confirmation.prorated_refund_amount;
      if (!cents || typeof cents !== 'number' || cents <= 0) return '';
      const dollars = cents / 100;
      return dollars.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.exporting = false;
        this.exportRequested = false;
        this.exportError = '';
        this.submitting = false;
        this.error_message = '';
        this.confirmation = null;
      }
    },
  },
  components: {},
  methods: {
    async confirm_cancel() {
      if (this.submitting) return;
      this.submitting = true;
      this.error_message = '';
      try {
        if (this.is_free_mode) {
          // FREE tenants have no Stripe subscription — fall back to the
          // legacy delete-account + logout flow.
          await this.$store.dispatch("Billing/delete_account");
          this.hide();
          await this.$store.dispatch("Authenticate/logout");
          return;
        }

        // CALCULATED tenants — schedule release. Backend keeps account
        // active for 30 days; we stay in the modal to show the user the
        // effective date + prorated refund. delete_account is NOT called
        // here — release_market handles its own cleanup.
        const result = await this.$store.dispatch("Market_billing/release");
        if (result && result.success) {
          this.confirmation = {
            cancel_effective_at: result.data && result.data.cancel_effective_at,
            prorated_refund_amount: result.data && result.data.prorated_refund_amount,
          };
        } else {
          this.error_message =
            (result && result.message) ||
            "We couldn't schedule cancellation. Please try again or contact support.";
        }
      } catch (err) {
        console.error('[cancel] confirm_cancel failed:', err);
        this.error_message =
          "We couldn't schedule cancellation. Please try again or contact support.";
      } finally {
        this.submitting = false;
      }
    },
    close: function () {
      this.hide();
    },
    async onRequestExport() {
      if (this.exporting || this.exportRequested) return;
      this.exporting = true;
      this.exportError = '';
      try {
        await requestDataExport();
        this.exportRequested = true;
      } catch (err) {
        console.error('Data export request failed:', err);
        this.exportError = "We couldn't start your export. Please try again or contact support@kiey.com.";
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
